import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import TopBar from './TopBar'
import BottomNav from './BottomNav'
import MoreMenu from './MoreMenu'
import Sidebar from './Sidebar'
import HelpAssistant from '../shared/HelpAssistant'

// Shell architecture (documented here so future changes don't regress):
//
//   <div h-dvh flex-col overflow-hidden>       <- document never scrolls
//     <TopBar flex-shrink-0 />
//     <div flex-1 min-h-0 flex>                <- row: sidebar + main
//       <Sidebar flex-shrink-0 overflow-y-auto>
//       <main flex-1 min-w-0 min-h-0 overflow-y-auto>
//         <Outlet />                           <- pages render here, main scrolls
//       </main>
//     </div>
//   </div>
//
// Consequences:
// - TopBar does not need position: sticky. It's the first child of a non-
//   scrolling column; it sits where it's rendered.
// - Sidebar does not need h-screen/sticky. It fills the flex row.
// - `<main>` is the ONLY route-level scroll container. Sticky elements
//   inside pages stick relative to main's content edge.
// - `min-h-0` and `min-w-0` on flex children are required — without them,
//   flex items refuse to shrink and the inner overflow becomes unreachable.
// - BottomNav is mobile-only, position:fixed from inside; unaffected.

export default function AppShell() {
  const [moreOpen, setMoreOpen] = useState(false)

  // Scopes the html/body scroll lock from index.css to only pages inside
  // the app shell. Public routes and login pages scroll the document
  // normally — without this toggle their content clips at the viewport.
  useEffect(() => {
    document.body.classList.add('app-shell-active')
    return () => document.body.classList.remove('app-shell-active')
  }, [])

  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-surface-container-low">
      <TopBar />
      <div className="flex-1 min-h-0 flex">
        <Sidebar />
        <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
          {/* min-h-full lets pages with bounded layouts (Chat, Inbox) still
              flex-fill via h-full/flex (their h-full resolves against the
              parent's computed height, which equals main when content
              doesn't grow), while pages with taller content actually extend
              the wrapper so pb-* reserves clearance BELOW scrolling content.
              h-full was subtly broken: box-sizing:border-box pinned the box
              at main's height and children overflowed past the border, so
              pb-[160px] reserved space inside a fixed box that content
              ignored. That's the BIG-100 last-row-under-bottom-nav bug.
              Bottom padding tokens live in src/index.css under @theme —
              changing --bottom-nav-height automatically reflows the
              clearance. Desktop has no bottom nav. */}
          {/* Arbitrary-value class uses underscores where calc() needs
              spaces around +/- (CSS spec). Tailwind converts the
              underscores to spaces at build time. */}
          <div className="min-h-full max-w-[1200px] mx-auto px-4 lg:px-8 pb-[calc(var(--bottom-nav-height)_+_var(--bottom-nav-breathing)_+_env(safe-area-inset-bottom))] lg:pb-10">
            <Outlet />
          </div>
        </main>
      </div>
      <div className="lg:hidden">
        <BottomNav onMoreClick={() => setMoreOpen(true)} />
        <MoreMenu isOpen={moreOpen} onClose={() => setMoreOpen(false)} />
      </div>
      <HelpAssistant />
    </div>
  )
}
